import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import {
  inputBaseStyles,
  inputSizeStyles,
  type InputSize,
} from '@components/ui/styles/input-styles';
import { cn } from '@components/utils/classname';

const inputVariants = cva(
  // Base styles + placeholder styles
  [...inputBaseStyles, 'placeholder:text-secondary-foreground'],
  {
    variants: {
      size: inputSizeStyles,
      error: {
        // Error state: destructive border (always, including focus)
        true: 'border-destructive',
        // Default: border change on focus only (no ring per Figma)
        false: 'border-input-border focus:border-ring',
      },
    },
    defaultVariants: {
      size: 'md',
      error: false,
    },
  },
);

export type { InputSize };

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {}

/**
 * Input component with focus behavior (per Figma XDS):
 * - Focus: Border change only (no ring)
 *
 * Sizes: xxs (24px), xs (28px), sm (32px), md (36px), lg (40px)
 * Error state: Uses destructive border color
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, error, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(inputVariants({ size, error }), className)}
        data-error={error ? 'true' : undefined}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export { Input, inputVariants };
